import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, MenuItem, Select, Typography } from "@mui/material";

import {
  DataGridCard,
  GridEmptyCard,
  LandingBalanceCard,
  ManualLoader,
  Messages,
  NotificationMessage,
  PendingApplicationsCard,
  WelcomeMessageModal,
  Wrapper,
} from "src/components";
import { ContentContainer } from "src/components/Layouts/AppShell";

import tokens from "src/shared/theme/tokens";
import { securityOptions } from "src/shared/config/storeConfig";
import {
  BannerCarousel,
  resolveHomeBannerSlides,
} from "src/components/Media/BannerCarousel";

import {
  RebindLeaseOption,
  useGetAutoPayQuery,
  useLazyGetBalanceDetailsQuery,
  useGetPaymentMethodsQuery,
  useGetUserQuery,
  useLazyGetWorkOrdersQuery,
  useLazyGetUserQuery,
  useGetDocsQuery,
  useLazyGetDocQuery,
  useGetMoveInConditionStatusQuery,
  useGetApplicationsByEmailQuery,
  useGetRenewalEligibilityQuery,
  useGetRenewalChoiceQuery,
  useGetRebindLeasesQuery,
} from "src/store/services/privateApi";
import {
  RebindConfirm,
  singleHomeRebindOffer,
} from "src/pages/Settings/Leases";
import {
  CONSTANT_PAYMENT_METHOD_STATUSES,
  CONSTANT_PAYMENT_STATUSES,
} from "src/shared/utilities/constants";
import { getAccountLinkUrl } from "src/helpers/helperRent";
import {
  findRenewalOfferDoc,
  getCurrentLease,
  isActiveLeaseStatus,
  isHomeLeaseSwitcherEnabled,
} from "src/helpers/helperLease";
import { selectPendingApplications } from "src/helpers/helperApplications";
import { IdentityNote } from "../Identity/IdentityNote";
import { IDENTITY_VERIFICATION_SCREEN } from "src/shared/models/containers/identity.model";
import { IdvFailureReason } from "src/utils/idvFailureReason";
import { VerifyIdentityScreen } from "../Identity/VerifyIdentity";
import { IdentityVerification } from "../Account/Reporting/Identity";
import { IdentityApproved } from "../Account/Reporting/Approved";
import { IdentityFailed } from "../Account/Reporting/Failed";
import { getFullName } from "src/helpers/helperSettings";
import { useFlags } from "launchdarkly-react-client-sdk";
import { getMoveInConditionAvailability } from "../Requests/MoveInCondition/moveInConditionAvailability";
import { StandbyDepositEmbed } from "src/components/StandbyDepositEmbed/StandbyDepositEmbed";

function LeaseSwitchValue({ lease }: { lease: RebindLeaseOption }) {
  return (
    <Box sx={{ minWidth: 0, pr: 0.5, textAlign: "left" }}>
      <Typography
        noWrap
        sx={{
          color: tokens.colors.ink,
          fontSize: 14,
          fontWeight: 600,
          lineHeight: 1.25,
        }}
      >
        {lease.propertyName || "Lease"}
      </Typography>
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          gap: 0.75,
          mt: 0.25,
        }}
      >
        {lease.unit && (
          <Typography
            noWrap
            sx={{
              color: tokens.colors.inkSecondary,
              fontSize: 12,
              lineHeight: 1.3,
            }}
          >
            {`Unit ${lease.unit}`}
          </Typography>
        )}
        {lease.status && (
          <Box
            component="span"
            sx={{
              bgcolor: lease.current
                ? tokens.colors.primarySoft
                : tokens.colors.neutral.bg,
              borderRadius: `${tokens.radius.pill}px`,
              color: lease.current
                ? tokens.colors.primaryDeep
                : tokens.colors.neutral.fg,
              flexShrink: 0,
              fontSize: 11,
              fontWeight: 600,
              lineHeight: 1,
              px: 0.75,
              py: "3px",
            }}
          >
            {lease.status}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export const HomeScreenContainer = () => {
  const navigate = useNavigate(); // init navigate from useNavigate hook
  const { t } = useTranslation(); // init t from useTranslation hook
  const location = useLocation();
  const { tpExistingUserIdentityVerification, tpHomeLeaseSwitcher } =
    useFlags();
  const leaseSwitcherOn = isHomeLeaseSwitcherEnabled(tpHomeLeaseSwitcher);

  const [errorMessage, setErrorMessage] = useState<string>(""); // state used to set the error message
  const [inProgressRequests, setInProgressRequests] = useState<any>([]); // state used to set onlythe inprogress status contain requests
  const [isActivePaymentMethodsAvailable, setIsActivePaymentMethodsAvailable] =
    useState(true); // state used to check active payment methods available or not
  const [welcomeMessageModalVisible, setWelcomeMessageModalVisible] =
    useState(false); // set state to handle the welcome modal visibility
  const [idvScreen, setIdvScreen] = useState<IDENTITY_VERIFICATION_SCREEN>(
    IDENTITY_VERIFICATION_SCREEN.NONE
  );
  const [isCompletedKYC, setIsCompletedKYC] = useState(false);
  const [idvFailureReason, setIdvFailureReason] =
    useState<IdvFailureReason | null>(null);
  const [idvFailureReasons, setIdvFailureReasons] = useState<
    IdvFailureReason[] | null
  >(null);

  const { isFromVerifiedEmail } = location.state
    ? (location.state as { isFromVerifiedEmail: boolean })
    : { isFromVerifiedEmail: false };

  // get the account-balance api details
  const [
    getBalanceDetails,
    {
      data: balanceData,
      isSuccess: isSuccessBalanceData,
      isLoading: isLoadingBalanceData,
      isError: isErrorBalanceData,
    },
  ]: any = useLazyGetBalanceDetailsQuery();

  // get the work orders (requests) list from api
  const [getWorkOrders, { data, isSuccess, isLoading, isError }]: any =
    useLazyGetWorkOrdersQuery();
  console.log("HomeScreenContainer");
  // get the user api details
  const {
    data: userData,
    isSuccess: isSuccessUserData,
    isLoading: isLoadingUserData,
    isError: isErrorUserData,
  }: any = useGetUserQuery(null);
  const [getUserLazyRequest]: any = useLazyGetUserQuery();

  const currentLease = getCurrentLease(userData);
  const { data: rebindLeases } = useGetRebindLeasesQuery(undefined, {
    skip: !isSuccessUserData || !leaseSwitcherOn,
  });
  const [homeRebindTarget, setHomeRebindTarget] =
    useState<ReturnType<typeof singleHomeRebindOffer>>(undefined);
  const switchLeases = rebindLeases?.leases ?? [];
  const canSwitchLease =
    leaseSwitcherOn &&
    Boolean(currentLease) &&
    switchLeases.some((lease) => !lease.current);
  const currentSwitchLease =
    switchLeases.find((lease) => lease.current) ?? switchLeases[0];
  const homeOffer = singleHomeRebindOffer(rebindLeases?.leases);
  const hasOtherRebindLeases = switchLeases.some((lease) => !lease.current);
  const homeOfferPlace = homeOffer
    ? homeOffer.propertyName
      ? `Unit ${homeOffer.unit} at ${homeOffer.propertyName}`
      : homeOffer.unit
      ? `Unit ${homeOffer.unit}`
      : homeOffer.personName
    : "";

  // The home banner rotation: the property's own uploaded banner followed by any
  // hardcoded promo campaign it hasn't opted out of, each filtered by its own
  // audience. A banner gated to "credbuild_unenrolled" is hidden from residents
  // already enrolled, and from pre-move-in residents (Pending/Future lease) who
  // can't enroll in rent reporting yet. Audience "all" shows to everyone.
  const isEnrolledInCredBuild = !!userData?.user?.isCreditReportingEnabled;
  const isActiveResident = isActiveLeaseStatus(currentLease?.Status);
  const homeBannerSlides = useMemo(
    () =>
      resolveHomeBannerSlides(currentLease?.property, {
        isEnrolledInCredBuild,
        isActiveResident,
      }),
    [currentLease?.property, isEnrolledInCredBuild, isActiveResident]
  );

  // Ops login-as has no Auth0 session; defined early so application selection
  // can skip the "already lives here" filter staff need when inspecting a lease.
  const isLoginAs = !!securityOptions.getLoginAsToken();

  // Pending Applications card: surface only actionable applications, hiding
  // stale ones for a property the resident already lives in. Under login-as,
  // keep apps for the active lease — that is usually what ops is inspecting.
  const residentEmail = userData?.user?.email ?? "";
  const { data: applicationsData } = useGetApplicationsByEmailQuery(
    residentEmail,
    { skip: !residentEmail }
  );
  const pendingApplications = useMemo(
    () =>
      selectPendingApplications(
        applicationsData ?? [],
        isLoginAs ? null : userData
      ),
    [applicationsData, userData, isLoginAs]
  );

  const { data: docsData } = useGetDocsQuery(null);
  const [getLazyDocQuery] = useLazyGetDocQuery();
  const { data: moveInConditionStatus } = useGetMoveInConditionStatusQuery(
    currentLease?.id ?? "",
    {
      skip: !currentLease?.id,
    }
  );

  const { data: renewalEligibility } = useGetRenewalEligibilityQuery(
    currentLease?.id ?? "",
    { skip: !currentLease?.id }
  );

  const { data: renewalChoiceData } = useGetRenewalChoiceQuery(
    currentLease?.id ?? "",
    { skip: !currentLease?.id }
  );
  const renewalChoice = renewalChoiceData?.choice ?? null;

  const moveInConditionAvailability = useMemo(
    () =>
      getMoveInConditionAvailability(
        currentLease?.MoveInDate,
        currentLease?.property?.state
      ),
    [currentLease?.MoveInDate, currentLease?.property?.state]
  );

  const showMoveInConditionBanner = Boolean(
    currentLease &&
      moveInConditionAvailability.isAvailable &&
      moveInConditionStatus?.submitted === false &&
      moveInConditionStatus?.window?.isOpen === true
  );

  const renewalOfferDoc = useMemo(() => {
    if (!currentLease || !docsData) return null;
    if (currentLease.Status !== "Current") return null;
    if (renewalEligibility?.reason === "renewal_accepted") return null;
    return findRenewalOfferDoc(docsData, currentLease.LeaseEndDate);
  }, [currentLease, docsData, renewalEligibility?.reason]);

  const showRenewalRequestForm = useMemo(() => {
    if (!currentLease) return false;
    if (renewalChoice) return false;
    if (renewalOfferDoc) return false;
    if (!renewalEligibility) return false;
    return (
      renewalEligibility.mode === "zoho" ||
      renewalEligibility.mode === "self_service"
    );
  }, [currentLease, renewalOfferDoc, renewalEligibility, renewalChoice]);

  const renewalSelfService = renewalEligibility?.mode === "self_service";
  const showRenewalOfferDoc = Boolean(renewalOfferDoc && !renewalChoice);

  const formatRenewalHomeDate = (iso: string | null | undefined) => {
    if (!iso) return "—";
    const local = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (local) {
      return new Date(
        Number(local[1]),
        Number(local[2]) - 1,
        Number(local[3])
      ).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatRenewalHomeRent = (amount: number | null | undefined) => {
    if (amount == null || Number.isNaN(Number(amount))) return "—";
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(Number(amount));
  };

  const renewalStatusBannerSx = {
    backgroundColor: tokens.colors.primarySoft,
    padding: "14px 20px",
    borderRadius: "12px",
    marginBottom: 2,
    border: `1px solid ${tokens.colors.primaryTint}`,
    display: "block",
    width: "100%",
    textAlign: "left" as const,
  };

  // Fetch balance details only if there's a current lease
  useEffect(() => {
    if (currentLease) {
      getBalanceDetails();
      getWorkOrders({ isDateFilterAvailable: false });
    }
  }, [currentLease, getBalanceDetails, getWorkOrders]);

  // get the auto-pay api details — skipped under ops login-as (payments Auth0-only)
  const {
    data: autoPayData,
    isSuccess: isSuccessAutoPay,
    isLoading: isLoadingAutoPay,
    isError: isErrorAutoPay,
  } = useGetAutoPayQuery(null, { skip: isLoginAs });

  // get the payment method api details — skipped under ops login-as
  const { data: paymentMethodsData, isSuccess: isSuccessPaymentMethods } =
    useGetPaymentMethodsQuery(null, { skip: isLoginAs });

  /**
   * Handle the error based on the dependency change
   */
  useEffect(() => {
    if (isSuccess && data && data.length > 0) {
      const getInProgressRequests = data.filter(
        (item: any) => item.Status === "In Progress"
      );

      if (getInProgressRequests && getInProgressRequests.length > 0) {
        setInProgressRequests(getInProgressRequests);
      } else {
        setInProgressRequests([]);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isSuccess]);

  /**
   * Check the active payment methods available or not
   */
  useEffect(() => {
    let activePaymentMethods = [];

    if (
      isSuccessPaymentMethods &&
      paymentMethodsData &&
      paymentMethodsData?.paymentMethods?.length > 0
    ) {
      activePaymentMethods = paymentMethodsData?.paymentMethods.filter(
        (item: { status: string }) =>
          item.status === CONSTANT_PAYMENT_METHOD_STATUSES.ACTIVE
      );
      if (activePaymentMethods && activePaymentMethods.length > 0) {
        setIsActivePaymentMethodsAvailable(true);
      } else {
        setIsActivePaymentMethodsAvailable(false);
      }
    }

    if (
      isSuccessPaymentMethods &&
      paymentMethodsData &&
      paymentMethodsData?.paymentMethods?.length === 0
    ) {
      setIsActivePaymentMethodsAvailable(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethodsData, isSuccessPaymentMethods]);

  /**
   * Handle the welcome message modal if user navigate from registration flow
   */
  useEffect(() => {
    if (isFromVerifiedEmail && isSuccessUserData) {
      if (userData && userData?.user) {
        setWelcomeMessageModalVisible(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessUserData, userData, isFromVerifiedEmail]);

  useEffect(() => {
    const isKycSucces = userData?.user?.isKycSuccess;
    if (isKycSucces) {
      setIdvScreen(IDENTITY_VERIFICATION_SCREEN.NONE);
    } else {
      setIdvScreen(IDENTITY_VERIFICATION_SCREEN.MESSAGE);
    }
    setIsCompletedKYC(isKycSucces);
    localStorage.setItem("isKycSucces", String(isKycSucces));
  }, [userData?.user?.isKycSuccess]);

  /**
   * Welcome message modal close event
   */

  const onWelcomeMessageModelCloseEvent = () => {
    setWelcomeMessageModalVisible(true);
    navigate("/home", { replace: true });
  };

  /**
   * Close Identity Verification Message
   */
  const handleShowNormalHomePage = () => {
    setIdvScreen(IDENTITY_VERIFICATION_SCREEN.NONE);
  };

  const handleIdentityApproved = async () => {
    const { data: updatedUserData } = await getUserLazyRequest();
    setIsCompletedKYC(updatedUserData?.user?.isKycSuccess);
    localStorage.setItem("isKycSucces", updatedUserData?.user?.isKycSuccess);
    setIdvScreen(IDENTITY_VERIFICATION_SCREEN.NONE);
  };

  /**
   * Click verify now on identity verification message
   */
  const onVerifyNow = () => {
    setIdvScreen(IDENTITY_VERIFICATION_SCREEN.Identity_Verification);
  };

  const onVerifyIdentityStart = () => {
    setIdvScreen(IDENTITY_VERIFICATION_SCREEN.Identity_Verification);
  };

  const onIdentityVerified = (
    status: string,
    failureReason?: IdvFailureReason | null,
    failureReasons?: IdvFailureReason[] | null
  ) => {
    if (status === "success") {
      setIdvFailureReason(null);
      setIdvFailureReasons(null);
      setIdvScreen(IDENTITY_VERIFICATION_SCREEN.IDENTITY_APPROVED);
    } else {
      setIdvFailureReason(failureReason ?? null);
      setIdvFailureReasons(failureReasons ?? null);
      setIdvScreen(IDENTITY_VERIFICATION_SCREEN.IDENTITY_FAILED);
    }
  };

  /**
   * Handle the Errors
   */
  useEffect(() => {
    if (isErrorUserData && !isSuccessUserData) {
      setErrorMessage("Unable to fetch the user information");
    } else if (isErrorBalanceData && !isSuccessBalanceData) {
      setErrorMessage("Unable to fetch the balance data");
    } else if (isError && !isSuccess) {
      setErrorMessage("Unable to fetch the work orders");
    } else if (isErrorAutoPay && !isSuccessAutoPay) {
      setErrorMessage("Unable to fetch the autopay data");
    } else {
      setErrorMessage("");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isErrorBalanceData,
    isError,
    isErrorAutoPay,
    isSuccessBalanceData,
    isSuccess,
    isErrorUserData,
    isSuccessUserData,
    isSuccessAutoPay,
  ]);

  const setBottomHeaderContent =
    isSuccess && userData
      ? `${currentLease?.property?.name} | Unit ${currentLease?.Unit}`
      : "";
  const residentName =
    isSuccessUserData && userData ? userData?.user?.person?.FirstName : "";

  if (idvScreen === IDENTITY_VERIFICATION_SCREEN.Identity_Verification) {
    return (
      <IdentityVerification
        phoneNumber={userData?.user?.phone}
        email={userData?.user?.email}
        fullName={getFullName(
          userData?.user?.person?.FirstName,
          userData?.user?.person?.LastName
        )}
        personId={userData?.user?.person?.id}
        onSubmitEvent={onIdentityVerified}
        onBackButtonClick={handleShowNormalHomePage}
      />
    );
  }

  if (idvScreen === IDENTITY_VERIFICATION_SCREEN.IDENTITY_APPROVED) {
    return <IdentityApproved onSubmitEvent={handleIdentityApproved} />;
  }

  if (idvScreen === IDENTITY_VERIFICATION_SCREEN.IDENTITY_FAILED) {
    return (
      <IdentityFailed
        onSubmitEvent={onVerifyIdentityStart}
        submitButtonText={t("buttonTexts.goBackButton")}
        failureReason={idvFailureReason}
        failureReasons={idvFailureReasons}
      />
    );
  }

  return (
    <>
      {(isLoadingUserData || isLoadingBalanceData) && <ManualLoader />}
      <Wrapper isAuthorizedWrapper idSelector={"wrapperContainer"}>
        {isSuccessUserData &&
        tpExistingUserIdentityVerification &&
        idvScreen === IDENTITY_VERIFICATION_SCREEN.MESSAGE ? (
          <ContentContainer size="default">
            <IdentityNote
              onVerifyNow={onVerifyNow}
              onCloseNote={handleShowNormalHomePage}
            />
          </ContentContainer>
        ) : (
          <ContentContainer size="default" sx={{ pb: 6 }}>
            {!!securityOptions.getLoginAsToken() && (
              <Box
                sx={{
                  mb: 2,
                  px: 2,
                  py: 1.5,
                  borderRadius: 1,
                  bgcolor: "warning.light",
                  color: "warning.contrastText",
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  Ops login-as session (read-only, 15 min). Payments are
                  disabled. Close this tab when done.
                </Typography>
              </Box>
            )}
            <BannerCarousel slides={homeBannerSlides} />
            <Box sx={{ width: "100%", mb: 3 }}>
              <Typography
                component="h1"
                variant="h6"
                textTransform={"capitalize"}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  color: tokens.colors.ink,
                  fontWeight: 600,
                  fontFamily: tokens.fonts.display,
                  letterSpacing: "-0.015em",
                }}
              >
                {`${!isLoadingUserData ? t("home.welcomeText") : ""} ${
                  residentName ?? ""
                }`}
              </Typography>
              {canSwitchLease && currentSwitchLease ? (
                <Box sx={{ maxWidth: 360, mt: 1 }}>
                  <Typography
                    sx={{
                      color: tokens.colors.inkMuted,
                      fontSize: 12,
                      fontWeight: 500,
                      mb: 0.5,
                    }}
                  >
                    {t("home.rebind.youAreOn", "You are on")}
                  </Typography>
                  <Select
                    fullWidth
                    variant="outlined"
                    value={currentSwitchLease.personId}
                    renderValue={() => (
                      <LeaseSwitchValue lease={currentSwitchLease} />
                    )}
                    onChange={(event) => {
                      const next = switchLeases.find(
                        (lease) => lease.personId === event.target.value
                      );
                      if (next && !next.current) setHomeRebindTarget(next);
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          border: `1px solid ${tokens.colors.border}`,
                          borderRadius: `${tokens.radius.md}px`,
                          boxShadow: tokens.shadows.overlay,
                          mt: 0.5,
                        },
                      },
                    }}
                    sx={{
                      bgcolor: tokens.colors.surface,
                      borderRadius: `${tokens.radius.sm}px`,
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: tokens.colors.border,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: tokens.colors.borderStrong,
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: tokens.colors.primary,
                        borderWidth: 1,
                      },
                      "& .MuiSelect-select": {
                        alignItems: "center",
                        display: "flex",
                        py: 1,
                        pl: 1.5,
                      },
                      "& .MuiSvgIcon-root": {
                        color: tokens.colors.inkSecondary,
                      },
                    }}
                  >
                    {switchLeases.map((lease) => (
                      <MenuItem
                        key={lease.personId}
                        value={lease.personId}
                        sx={{
                          py: 1,
                          "&.Mui-selected": {
                            bgcolor: tokens.colors.primarySoft,
                          },
                          "&.Mui-selected:hover": {
                            bgcolor: tokens.colors.primarySoft,
                          },
                        }}
                      >
                        <LeaseSwitchValue lease={lease} />
                      </MenuItem>
                    ))}
                  </Select>
                  {homeRebindTarget && (
                    <RebindConfirm
                      lease={homeRebindTarget}
                      current={rebindLeases?.current}
                      onCancel={() => setHomeRebindTarget(undefined)}
                    />
                  )}
                  <Typography
                    component="button"
                    type="button"
                    onClick={() => navigate("/settings/leases")}
                    sx={{
                      mt: 1,
                      p: 0,
                      border: 0,
                      background: "none",
                      cursor: "pointer",
                      color: "primary.main",
                      fontSize: 13,
                      textDecoration: "underline",
                      textAlign: "left",
                      "&:hover": { color: "primary.dark" },
                    }}
                  >
                    {t("home.rebind.chooseLease", "Choose a lease")}
                  </Typography>
                </Box>
              ) : (
                setBottomHeaderContent && (
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: tokens.colors.inkSecondary,
                      mt: 0.25,
                    }}
                  >
                    {setBottomHeaderContent}
                  </Typography>
                )
              )}
            </Box>

            {currentLease?.id ? (
              <StandbyDepositEmbed
                leaseId={currentLease.id}
                propertyAbbreviation={currentLease?.property?.abbreviation}
              />
            ) : null}

            {showMoveInConditionBanner && (
              <Box
                sx={{
                  alignItems: { xs: "stretch", sm: "center" },
                  backgroundColor: tokens.colors.warning.bg,
                  border: `1px solid ${tokens.colors.warning.main}`,
                  borderRadius: 1,
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 1.5, sm: 2 },
                  justifyContent: "space-between",
                  marginBottom: 2,
                  padding: { xs: 2, sm: "16px 20px" },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: tokens.colors.ink,
                      fontFamily: tokens.fonts.display,
                      fontSize: 15,
                      fontWeight: 700,
                      lineHeight: 1.25,
                      mb: 0.5,
                    }}
                  >
                    {t("home.moveInConditionBanner.title")}
                  </Typography>
                  <Typography
                    sx={{
                      color: tokens.colors.inkSecondary,
                      fontFamily: tokens.fonts.body,
                      fontSize: 14,
                      lineHeight: 1.45,
                    }}
                  >
                    {t("home.moveInConditionBanner.content", {
                      dueDate: moveInConditionAvailability.dueDateLabel,
                    })}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate("/requests/move-in-condition")}
                  sx={{
                    flexShrink: 0,
                    minHeight: 40,
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("home.moveInConditionBanner.button")}
                </Button>
              </Box>
            )}

            {renewalChoice?.path === "ntv" && (
              <Box sx={renewalStatusBannerSx}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "14px",
                    color: tokens.colors.ink,
                    mb: 0.5,
                  }}
                >
                  {t("renewal.homeStatus.ntvTitle", {
                    moveOutDate: formatRenewalHomeDate(
                      renewalChoice.moveOutDate
                    ),
                  })}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "13px",
                    color: tokens.colors.inkSecondary,
                  }}
                >
                  {t("renewal.homeStatus.ntvSecondary")}
                </Typography>
              </Box>
            )}

            {renewalChoice?.path === "renew" && (
              <Box sx={renewalStatusBannerSx}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "14px",
                    color: tokens.colors.ink,
                    mb: 1,
                  }}
                >
                  {t("renewal.homeStatus.renewTitle", {
                    months: renewalChoice.termMonths ?? "—",
                    rent: formatRenewalHomeRent(renewalChoice.termRent),
                    dateRange:
                      renewalChoice.termStart && renewalChoice.termEnd
                        ? `${formatRenewalHomeDate(
                            renewalChoice.termStart
                          )} – ${formatRenewalHomeDate(renewalChoice.termEnd)}`
                        : "—",
                  })}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => navigate("/renewal/renew/next")}
                  >
                    {t("renewal.homeStatus.renewContinue")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => navigate("/renewal/renew")}
                    sx={{ borderColor: tokens.colors.primary }}
                  >
                    {t("renewal.homeStatus.renewChangeTerm")}
                  </Button>
                </Box>
              </Box>
            )}

            {showRenewalRequestForm && (
              <Box
                component={renewalSelfService ? "button" : "a"}
                {...(renewalSelfService
                  ? {
                      type: "button",
                      onClick: () => navigate("/renewal"),
                    }
                  : {
                      href: "https://forms.zohopublic.com/resprop/form/RenewalRequestFormTest/formperma/hlNbFU2wQM9Q8s0OjmD3Exr-V_3oxRNqaeJQjkb8j7k",
                      target: "_blank",
                      rel: "noopener noreferrer",
                    })}
                sx={{
                  backgroundColor: tokens.colors.primarySoft,
                  padding: "14px 20px",
                  borderRadius: "12px",
                  marginBottom: 2,
                  border: `1px solid ${tokens.colors.primaryTint}`,
                  display: "block",
                  textDecoration: "none",
                  color: "inherit",
                  cursor: "pointer",
                  width: "100%",
                  textAlign: "left",
                  font: "inherit",
                  "&:hover": {
                    backgroundColor: tokens.colors.primaryTint,
                  },
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 700,
                    fontSize: "14px",
                    color: tokens.colors.ink,
                  }}
                >
                  {t("home.renewalBanner.title")}{" "}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 500,
                    fontSize: "14px",
                    color: tokens.colors.primary,
                  }}
                >
                  {renewalSelfService
                    ? t("renewal.banner.selfServiceLink")
                    : t("home.renewalBanner.link")}
                </Typography>
              </Box>
            )}

            {showRenewalOfferDoc && (
              <Box
                component={renewalSelfService ? "button" : "a"}
                {...(renewalSelfService
                  ? {
                      type: "button",
                      onClick: () => navigate("/renewal"),
                    }
                  : {
                      href: "#",
                      onClick: async (e: React.MouseEvent) => {
                        e.preventDefault();
                        const res: any = await getLazyDocQuery(
                          renewalOfferDoc.documentId
                        );
                        if (!res.isError) {
                          window.open(res.data.uri, "_blank");
                        }
                      },
                    })}
                sx={{
                  backgroundColor: tokens.colors.primarySoft,
                  padding: "14px 20px",
                  borderRadius: "12px",
                  marginBottom: 2,
                  border: `1px solid ${tokens.colors.primaryTint}`,
                  display: "block",
                  textDecoration: "none",
                  color: "inherit",
                  cursor: "pointer",
                  width: "100%",
                  textAlign: "left",
                  font: "inherit",
                  "&:hover": {
                    backgroundColor: tokens.colors.primaryTint,
                  },
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 700,
                    fontSize: "14px",
                    color: tokens.colors.ink,
                  }}
                >
                  {t("home.renewalBanner.title")}
                </Typography>{" "}
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 500,
                    fontSize: "14px",
                    color: tokens.colors.primary,
                  }}
                >
                  {t("home.renewalBanner.reviewOffer")}
                </Typography>
              </Box>
            )}

            {isSuccessUserData &&
              !isCompletedKYC &&
              tpExistingUserIdentityVerification && (
                <VerifyIdentityScreen onNextScreen={onVerifyIdentityStart} />
              )}

            <Box sx={{ position: "relative" }}>
              {isSuccessUserData &&
                !isCompletedKYC &&
                tpExistingUserIdentityVerification && (
                  <Box
                    sx={{
                      position: "absolute",
                      width: "100%",
                      height: "calc(100% - 24px)",
                      background: tokens.colors.border,
                      opacity: 0.5,
                      zIndex: 1,
                    }}
                  />
                )}

              {!currentLease && isSuccessUserData && !isLoadingUserData && (
                <Box
                  sx={{
                    backgroundColor: tokens.colors.pageBg,
                    padding: 4,
                    borderRadius: 2,
                    marginBottom: 3,
                    border: `1px solid ${tokens.colors.border}`,
                    textAlign: "center",
                  }}
                >
                  {homeOffer || hasOtherRebindLeases ? (
                    <>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          marginBottom: 2,
                          fontSize: "18px",
                          color: "text.primary",
                        }}
                      >
                        {t(
                          "home.rebind.noLeaseTitle",
                          "Connect this login to a lease"
                        )}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          lineHeight: 1.6,
                          marginBottom: 2,
                        }}
                      >
                        {homeOffer
                          ? t(
                              "home.rebind.noLeaseSingle",
                              "This login is not on a lease yet. Connect it to {{place}} to pay and view that account.",
                              { place: homeOfferPlace }
                            )
                          : t(
                              "home.rebind.noLeaseChoose",
                              "This login is not on a lease yet. Choose the lease you need to pay or view."
                            )}
                      </Typography>
                      {homeOffer ? (
                        <Box sx={{ mt: 2 }}>
                          {homeRebindTarget ? (
                            <RebindConfirm
                              lease={homeRebindTarget}
                              onCancel={() => setHomeRebindTarget(undefined)}
                            />
                          ) : (
                            <Button
                              variant="contained"
                              onClick={() => setHomeRebindTarget(homeOffer)}
                            >
                              {t(
                                "home.rebind.homeOffer",
                                "Connect to {{target}}",
                                { target: homeOfferPlace }
                              )}
                            </Button>
                          )}
                        </Box>
                      ) : (
                        <Button
                          sx={{ mt: 1 }}
                          variant="contained"
                          onClick={() => navigate("/settings/leases")}
                        >
                          {t("home.rebind.chooseLease", "Choose a lease")}
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 600,
                          marginBottom: 2,
                          fontSize: "18px",
                          color: "text.primary",
                        }}
                      >
                        {t("home.noLeaseMessage.title", "No Active Lease")}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          lineHeight: 1.6,
                          marginBottom: 2,
                        }}
                      >
                        {t(
                          "home.noLeaseMessage.description",
                          "You currently don't have an active or upcoming lease with us. Please "
                        )}
                        <Typography
                          component="a"
                          variant="body2"
                          onClick={() => navigate("/settings/question")}
                          sx={{
                            color: "primary.main",
                            textDecoration: "underline",
                            cursor: "pointer",
                            "&:hover": {
                              color: "primary.dark",
                            },
                          }}
                        >
                          {t(
                            "home.noLeaseMessage.contactLink",
                            "reach out to us"
                          )}
                        </Typography>
                        {t(
                          "home.noLeaseMessage.descriptionEnd",
                          " if you believe this is an error."
                        )}
                      </Typography>
                      {pendingApplications.length === 0 && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            lineHeight: 1.6,
                            marginBottom: 2,
                          }}
                        >
                          {t(
                            "home.noLeaseMessage.viewApplications",
                            "If you have applied with us, please view your applications "
                          )}
                          <Typography
                            component="a"
                            variant="body2"
                            onClick={() => navigate("/settings/applications")}
                            sx={{
                              color: "primary.main",
                              textDecoration: "underline",
                              cursor: "pointer",
                              display: "inline-block",
                              marginTop: 1,
                              "&:hover": {
                                color: "primary.dark",
                              },
                            }}
                          >
                            {t("home.noLeaseMessage.here", "here")}
                          </Typography>
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}

              {pendingApplications.length > 0 && (
                <PendingApplicationsCard
                  applications={pendingApplications}
                  navigate={navigate}
                  t={t}
                />
              )}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "3fr 2fr" },
                  gap: { xs: 0, md: 3 },
                  alignItems: "start",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  {currentLease && (
                    <LandingBalanceCard
                      balanceDetails={balanceData}
                      autoPayDetails={autoPayData}
                      onclickPayNowButton={() =>
                        navigate("/rent/payment", {
                          state: { isFromHome: true },
                        })
                      }
                      onclickScheduleButton={() =>
                        navigate("/rent/payment#auto-pay", {
                          state: { isFromHome: true },
                        })
                      }
                      isBalanceDetailLoadingSuccess={
                        isSuccessBalanceData && balanceData
                      }
                      isAutoPayDetailLoadingSuccess={
                        isSuccessAutoPay && autoPayData
                      }
                      isLoadingFadeEnabled={
                        isLoadingAutoPay || isLoadingBalanceData
                      }
                      t={t}
                    />
                  )}

                  {currentLease &&
                    isSuccessPaymentMethods &&
                    !isActivePaymentMethodsAvailable && (
                      <NotificationMessage
                        messageHeading={t(
                          "home.missingPaymentMethodsMessage.heading"
                        )}
                        messageContent={t(
                          "home.missingPaymentMethodsMessage.content"
                        )}
                        messageLinkEvent={() =>
                          navigate(
                            getAccountLinkUrl(balanceData?.paymentStatus),
                            {
                              state: {
                                isFromHome: true,
                                isOnlyCard:
                                  balanceData?.paymentStatus ===
                                  CONSTANT_PAYMENT_STATUSES.CERTIFIED_FUNDS,
                              },
                            }
                          )
                        }
                        variant="no_payment_methods"
                        topMargin={0}
                      />
                    )}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  {
                    /**
                     * display DataGridCard component with inprogress-requests data
                     * If transaction data length is more than 0 &&  api data loading is done
                     */
                    isSuccess &&
                      inProgressRequests &&
                      inProgressRequests.length > 0 &&
                      !isLoading && (
                        <DataGridCard
                          t={t}
                          dataObject={inProgressRequests}
                          dataGridCardTitle={t(
                            "requests.requestActionCard.inProgressCardTitle"
                          )}
                          isRecentWorkOrders
                          isCardLinkAvailable
                          navigate={navigate}
                          isTooltipInfoEnabled
                          tooltipContent={t("requests.common.tooltipContent")}
                          gridType="request"
                          iconArrowVisible={true}
                          isHandleArrowClickable={true}
                        />
                      )
                  }

                  {
                    /**
                     * display GridEmptyCard component for empty inprogress-requests data with empty state content
                     * this component will display after api data loading is done
                     */
                    isSuccess &&
                      inProgressRequests &&
                      inProgressRequests.length === 0 &&
                      !isLoading && (
                        <GridEmptyCard
                          title={t(
                            "requests.requestActionCard.inProgressCardTitle"
                          )}
                          emptyStateContent={t(
                            "requests.requestActionCard.emptyCardContent"
                          )}
                          isTooltipInfoEnabled
                          tooltipContent={t("requests.common.tooltipContent")}
                          onClickBtn={() =>
                            navigate("/requests/maintenance/create", {
                              state: { isFromHome: true },
                            })
                          }
                          buttonText={t(
                            "requests.requestActionCard.emptyCardBtntext"
                          )}
                        />
                      )
                  }

                  {
                    /**
                     * display GridEmptyCard component without empty state content
                     * this component will display while api data loading
                     */
                    isLoading && !isSuccess && (
                      <GridEmptyCard
                        title={t(
                          "requests.requestActionCard.inProgressCardTitle"
                        )}
                        isEmptyCardDataVisble={false}
                        isTooltipInfoEnabled
                        tooltipContent={t("requests.common.tooltipContent")}
                        isLoadingFadeEnabled={isLoadingUserData}
                        isInnerLoaderEnabled={!isLoadingUserData && isLoading}
                      />
                    )
                  }
                </Box>
              </Box>

              {errorMessage !== "" && (
                <Messages
                  topMargin={6}
                  messageContent={errorMessage}
                  closeEvent={() => setErrorMessage("")}
                />
              )}
            </Box>
          </ContentContainer>
        )}
      </Wrapper>

      {welcomeMessageModalVisible && isFromVerifiedEmail && (
        <WelcomeMessageModal
          t={t}
          ResidentName={residentName}
          closeIdModalEvent={onWelcomeMessageModelCloseEvent}
        />
      )}
    </>
  );
};
